/**
 * The hosted chat page - play.joacdn.com/{stream_id}/chat
 *
 * Chat on its own, no video: for customers to put in an iframe next to any
 * player (their own, YouTube's, none). The same chat code as the hosted
 * player, and the same public endpoints any developer's own UI uses.
 * Options on the URL (all optional):
 *   ?lang=tr|en       language (default: the browser's)
 *   ?accent=ff7a00    replaces the JustOnAir lime (6 hex digits)
 *   ?reactions=0      no reaction bar (you draw reactions over your own video)
 */
import '@fontsource-variable/hanken-grotesk/wght.css';
import { startChat } from './chat';

const API = (import.meta.env.VITE_API_URL || 'https://api.justonair.com').replace(/\/$/, '');
const READ = (import.meta.env.VITE_EMBED_URL || '/api/embed').replace(/\/$/, '');

const params = new URLSearchParams(location.search);
// /{stream_id}/chat in production; /chat.html?v={stream_id} works too (local dev).
const first = decodeURIComponent(location.pathname.split('/').filter(Boolean)[0] ?? '');
const streamId = first.startsWith('str_') ? first : params.get('v') || '';
const accent = /^[0-9a-f]{6}$/i.test(params.get('accent') ?? '') ? `#${params.get('accent')}` : null;
const host = document.getElementById('chat')!;

/** Readable text colour on a custom accent (as in the player). */
function inkFor(hex: string): string {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((c) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
  );
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.35 ? '#0e1626' : '#ffffff';
}

function notFound() {
  host.innerHTML = '<p style="margin:0;padding:32px 20px;text-align:center;color:#9aa8c0;font:15px system-ui,sans-serif">This chat could not be found. Check the link you were given.</p>';
}

async function main() {
  if (!/^str_[a-z0-9]{8,40}$/.test(streamId)) return notFound();
  // One look first, so a wrong id says so instead of "reconnecting" forever.
  const r = await fetch(`${READ}/${streamId}/chat`, { credentials: 'omit' }).catch(() => null);
  if (r?.status === 404) return notFound();
  startChat({
    streamId,
    api: API,
    read: READ,
    host,
    lang: params.get('lang'),
    reactions: params.get('reactions') !== '0',
    accent: accent ? { color: accent, ink: inkFor(accent) } : null,
  });
}
void main();
